/**
 * Conteúdo de tela do tema Coração da Morena (o motor não sabe nada disso).
 */
import type { Mode, ThemeId } from '@tigrinho/engine';
import type { Phrase, TauntItem } from '@tigrinho/server/protocol';

export interface Pawn {
  emoji: string;
  name: string;
}

export const PAWNS: Pawn[] = [
  { emoji: '🫏', name: 'Jegue de Óculos' },
  { emoji: '🕊️', name: 'Pombo Ostentação' },
  { emoji: '👵', name: 'Vó do Zap' },
  { emoji: '🐕', name: 'Caramelo' },
  { emoji: '🐓', name: 'Galo Cantor' },
  { emoji: '💁‍♀️', name: 'Tia da Fofoca' },
];

/**
 * Cor de cada assento (ordem de entrada na sala): azul, vermelho, verde e amarelo, as quatro
 * cores mais diferentes entre si (pedido do Matheus em 20/10: "cores completamente contrastantes").
 */
export const SEAT_COLORS = ['#1f6bff', '#e3262f', '#14a13f', '#ffc400'];
/** Versão clara da cor de cada assento (fundo de casas compradas). */
export const SEAT_TINTS = ['#d9e6ff', '#ffdcdd', '#d3f5dd', '#fff1bf'];
/** Cor do texto em cima da cor do assento (o amarelo pede texto escuro). */
export const SEAT_INKS = ['#fff', '#fff', '#fff', '#2a1a00'];
/** Nome da cor, para quem não distingue bem as cores. */
export const SEAT_COLOR_NAMES = ['azul', 'vermelho', 'verde', 'amarelo'];

/**
 * Cor da faixa de cada grupo de cidades (do mais barato ao mais caro). Oito tons bem
 * separados no círculo de cores, para nenhum grupo parecer com o vizinho.
 */
export const GROUP_COLORS: Record<number, string> = {
  1: '#8a5a32', // marrom
  2: '#17c3e6', // ciano
  3: '#ff3fa4', // rosa-choque
  4: '#ff8a00', // laranja
  5: '#d7192b', // vermelho
  6: '#ffd400', // amarelo
  7: '#1fbf4a', // verde
  8: '#7a2ff0', // roxo
};

export const MODE_LABELS: Record<Mode, { name: string; minutes: number; hint: string }> = {
  relampago: { name: 'Relâmpago', minutes: 15, hint: 'Partida curta: acaba em até 15 minutos' },
  padrao: { name: 'Padrão', minutes: 30, hint: 'Acaba em até 30 minutos (ou antes, se alguém fechar a vitória)' },
  classico: { name: 'Clássico', minutes: 60, hint: 'Sem pressa: até 60 minutos' },
};

export const THEME_LABELS: Record<ThemeId, string> = {
  coracaoDaMorena: 'Coração da Morena',
  brasil: 'Brasil de Ponta a Ponta',
  roleDaGalera: 'Rolê da Galera',
  brasilXMundo: 'Brasil x Mundo',
};

export const TENSION: Record<1 | 2 | 3 | 4, { name: string; line: string }> = {
  1: { name: 'Calmaria', line: 'Carro do ovo passando, tudo tranquilo' },
  2: { name: 'Esquentando', line: 'O pagodão da vizinha começou' },
  3: { name: 'Treta', line: 'Apagão no bairro e sirene ao longe' },
  4: { name: 'Caos final', line: 'Disco voador e chuva de boleto' },
};

/** Nomes dos níveis de construção: "fulano só colocou a bandeirola". */
export const LEVEL_NAMES = ['Bandeirola', 'Casa 1', 'Casa 2', 'Casa 3', 'Hotel'] as const;

export const TAUNTS: { id: TauntItem; emoji: string; name: string }[] = [
  { id: 'tomate', emoji: '🍅', name: 'Tomate' },
  { id: 'ovo', emoji: '🥚', name: 'Ovo' },
  { id: 'chinelo', emoji: '🩴', name: 'Chinelo da mãe' },
  { id: 'torta', emoji: '🥧', name: 'Torta na cara' },
  { id: 'boleto', emoji: '🧾', name: 'Boleto na testa' },
  { id: 'risada', emoji: '😂', name: 'Risada' },
  { id: 'caveira', emoji: '💀', name: 'Caveira' },
  { id: 'palhaco', emoji: '🤡', name: 'Palhaço' },
  { id: 'choro', emoji: '😭', name: 'Choro' },
  { id: 'fogo', emoji: '🔥', name: 'Fogo' },
  { id: 'coroa', emoji: '👑', name: 'Coroa' },
];

export const TAUNT_EMOJI: Record<string, string> = Object.fromEntries(TAUNTS.map((t) => [t.id, t.emoji]));

export const PHRASE_TEXT: Record<Phrase, string> = {
  pagaLogo: 'Paga logo!',
  choraNao: 'Chora não',
  f: 'F total',
  vinganca: 'A vingança vem',
  taCaro: 'Tá caro, é?',
  bomDia: 'Bom dia, investidor',
  socorro: 'Socorro',
  gg: 'Legaaaal',
};

export interface CardText {
  title: string;
  text: string;
  deck: 'sorte' | 'reves' | 'caos';
}

export const CARD_TEXT: Record<string, CardText> = {
  fazendeiro: { deck: 'sorte', title: 'Carta Fazendeiro', text: 'Escolha uma cidade sua: o MST nunca vai invadir.' },
  facaChorar: { deck: 'sorte', title: 'Parabéns, faça seu amigo chorar', text: 'Derrube 1 casa de um oponente. Hotel não cai.' },
  pixErrado: { deck: 'sorte', title: 'Pix errado', text: 'Um oponente à sua escolha te manda 100 mil contos "sem querer".' },
  fiscal: { deck: 'sorte', title: 'Fiscal da Prefeitura', text: 'Interdite 1 propriedade de um oponente: ela não cobra aluguel por 1 rodada.' },
  heranca: { deck: 'sorte', title: 'Herança da tia-avó que ninguém conhecia', text: 'Receba 200 mil contos.' },
  vaquinha: { deck: 'sorte', title: 'Vaquinha do seu aniversário', text: 'Cada jogador te paga 50 mil contos.' },
  propagandaBet: { deck: 'sorte', title: 'Fez propaganda de bet', text: 'O tigrinho pagou bem: cada jogador te paga 75 mil contos.' },
  compraForcada: { deck: 'sorte', title: 'Compra forçada', text: 'Compre uma cidade só com bandeirola de um oponente, pagando 150% do terreno a ele.' },
  carona: { deck: 'sorte', title: 'Carona grátis', text: 'Vá para qualquer resort. Se estiver livre, pode comprar.' },
  trocaLugar: { deck: 'sorte', title: 'Troca de lugar', text: 'Troque de posição com qualquer jogador. Os dois resolvem a casa nova.' },
  advogado: { deck: 'sorte', title: 'Advogado de porta de cadeia', text: 'Guarde: sai da Ilha de graça.' },
  seguro: { deck: 'sorte', title: 'Seguro Anti-Zoeira', text: 'Guarde: bloqueia o próximo ataque contra você.' },
  reforma: { deck: 'sorte', title: 'Reforma no capricho', text: 'Suba 1 nível de graça numa cidade sua (até a casa 3).' },
  blackFriday: { deck: 'sorte', title: 'Black Friday', text: 'Guarde: sua próxima construção sai pela metade.' },
  cupom: { deck: 'sorte', title: 'Cupom de desconto', text: 'Guarde: o próximo aluguel que você pagar sai pela metade.' },
  achouDinheiro: { deck: 'sorte', title: 'Achou dinheiro na calça', text: 'Receba 50 mil contos.' },
  impostoBilionario: { deck: 'sorte', title: 'Imposto do bilionário', text: 'O jogador mais rico paga 150 mil contos ao mais pobre.' },
  viralizou: { deck: 'sorte', title: 'Viralizou', text: 'Na próxima rodada, seus aluguéis valem o dobro.' },
  voltaInicio: { deck: 'sorte', title: 'Volta ao início', text: 'Vá para o Início e receba 300 mil contos.' },
  ladraoGalinha: { deck: 'sorte', title: 'Ladrão de galinha', text: 'Roube 1 carta guardada de um oponente.' },
  multaRadar: { deck: 'reves', title: 'Multa do radar', text: 'Pague 100 mil contos.' },
  golpeParente: { deck: 'reves', title: 'Golpe do falso parente', text: '"Oi, troquei de número." Pague 150 mil contos.' },
  boletoEsquecido: { deck: 'reves', title: 'Boleto esquecido', text: 'Pague 10% do seu dinheiro (mínimo 50 mil contos).' },
  camburao: { deck: 'reves', title: 'Camburão', text: 'Vá direto para a Ilha, sem passar pelo Início.' },
  cupim: { deck: 'reves', title: 'Cupim', text: 'Perca 1 casa sua (você escolhe qual).' },
  manutencao: { deck: 'reves', title: 'Manutenção geral', text: 'Pague 25 mil contos por casa e 100 mil contos por hotel.' },
  rodizio: { deck: 'reves', title: 'Rodízio por sua conta', text: 'Pague 50 mil contos a cada jogador.' },
  exApareceu: { deck: 'reves', title: 'O ex apareceu', text: 'Pague 100 mil contos ao jogador mais pobre.' },
  esqueceuCarteira: { deck: 'reves', title: 'Esqueceu a carteira', text: 'Volte 3 casas.' },
  leaoReceita: { deck: 'reves', title: 'Leão da Receita', text: 'Pague 200 mil contos ou 10% do seu patrimônio, o que for menor.' },
  celularPrivada: { deck: 'reves', title: 'Celular caiu na privada', text: 'Perca 1 carta guardada (se tiver) e pague 50 mil contos.' },
  condominio: { deck: 'reves', title: 'Condomínio atrasado', text: 'Pague 75 mil contos por grupo completo que você tem.' },
  pomboAntena: { deck: 'reves', title: 'Pombo na antena', text: 'Sua cidade mais cara não cobra aluguel na próxima rodada.' },
  busaoQuebrou: { deck: 'reves', title: 'Busão quebrou', text: 'Na próxima jogada, você anda com 1 dado só.' },
  terremoto: { deck: 'caos', title: 'Terremoto', text: 'Todo mundo perde 1 casa, inclusive você. Hotel não cai.' },
  apocalipse: { deck: 'caos', title: 'Apocalipse Zoeiro', text: 'Derrube até 2 casas de oponentes, em cidades diferentes.' },
  apagao: { deck: 'caos', title: 'Apagão', text: 'Na próxima rodada, ninguém cobra aluguel.' },
  inflacao: { deck: 'caos', title: 'Inflação', text: 'Todos os aluguéis +50% por 2 rodadas.' },
  calote: { deck: 'caos', title: 'Calote', text: 'Guarde: você não paga o próximo aluguel.' },
  assalto: { deck: 'caos', title: 'Assalto à banca', text: 'Receba 300 mil contos do banco. O jogador mais rico perde 100 mil contos.' },
};

export const HELD_TEXT: Record<string, string> = {
  advogado: 'Habeas Corpus',
  seguro: 'Seguro Anti-Zoeira',
  blackFriday: 'Black Friday',
  cupom: 'Cupom',
  calote: 'Calote',
  fazendeiro: 'Fazendeiro',
  contador: 'Contador',
};

export const HELD_EMOJI: Record<string, string> = {
  advogado: '⚖️',
  seguro: '🛡️',
  blackFriday: '🏷️',
  cupom: '🎟️',
  calote: '🏃',
  fazendeiro: '🤠',
  contador: '🧮',
};

/** Lojinha: o que cada carta faz (preços em SHOP, no motor). */
export const SHOP_TEXT: Record<string, { title: string; text: string }> = {
  advogado: { title: 'Habeas Corpus', text: 'Sai da cadeia na hora, até da pena por sonegação (fiança de 600 mil).' },
  fazendeiro: { title: 'Fazendeiro', text: 'Chega com a liminar e o cachorro e expulsa a próxima invasão do MST de graça.' },
  seguro: { title: 'Seguro Anti-Zoeira', text: 'Bloqueia o próximo ataque de carta contra você.' },
  cupom: { title: 'Cupom de desconto', text: 'O próximo aluguel que você pagar sai pela metade.' },
  contador: { title: 'Contador', text: 'Livra você do próximo Faz o L: não paga o imposto.' },
};

/** "F total ou Legaaaal": as boas são Legaaaal, as ruins F total. */
export const DECK_LABEL = { sorte: 'Legaaaal', reves: 'F total', caos: 'Caos' } as const;

/** Texto curto de cada canto e casa especial, para a ficha da casa. */
export const SPECIAL_LINES: Record<string, string> = {
  start: 'Passou por aqui, ganhou 300 mil contos.',
  island: 'Ficou preso? Tire uma dupla (3 tentativas), pague 200 mil contos ou use o Advogado.',
  championship: 'Escolha uma cidade sua para sediar o Campeonato: o aluguel dela dobra (e sobe mais a cada vez).',
  worldTour: 'Na próxima vez, voe para qualquer casa livre ou sua por 50 mil contos.',
  chance: 'Puxe uma carta: Legaaaal (das boas) ou F total (das ruins).',
  tax: 'Imposto: 10% do valor de tudo o que você tem construído.',
};
